import axiosInstance from './axiosInstance';
import { decodeJwtToken } from '../utils/jwtUtils';

// Helper to get current user ID from token
const getCurrentUserId = () => {
  const rawToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || localStorage.getItem('token') || sessionStorage.getItem('token');
  const token = rawToken ? rawToken.trim().replace(/^"|"$/g, '').replace(/^Bearer\s+/i, '') : null;
  if (!token) return null;
  const decoded = decodeJwtToken(token);
  return decoded?.id || decoded?.userId || null;
};

// API Base path with context
const API_BASE = '/Corrohealth';

// ========================================
// JOB REQUISITIONS API
// ========================================
export const jobRequisitionApi = {
  getAll: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-requisitions`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-requisitions/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getByCode: async (code) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-requisitions/code/${code}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (data) => {
    try {
      const payload = {
        ...data,
        createdBy: getCurrentUserId()
      };
      const response = await axiosInstance.post(`${API_BASE}/api/job-requisitions`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const payload = {
        ...data,
        updatedBy: getCurrentUserId()
      };
      const response = await axiosInstance.put(`${API_BASE}/api/job-requisitions/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/job-requisitions/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateStatus: async (id, status) => {
    try {
      const response = await axiosInstance.patch(`${API_BASE}/api/job-requisitions/${id}/status`, {
        status,
        updatedBy: getCurrentUserId()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  assignRecruiter: async (id, recruiterId, recruiterName) => {
    try {
      const response = await axiosInstance.patch(`${API_BASE}/api/job-requisitions/${id}/recruiter`, {
        recruiterId,
        recruiterName,
        updatedBy: getCurrentUserId()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getOpenRequisitions: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-requisitions/open`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDashboardStats: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-requisitions/dashboard/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreate: async (requisitions) => {
    try {
      const payload = requisitions.map(r => ({
        ...r,
        createdBy: getCurrentUserId()
      }));
      const response = await axiosInstance.post(`${API_BASE}/api/job-requisitions/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkUpdateStatus: async (ids, status) => {
    try {
      const response = await axiosInstance.patch(`${API_BASE}/api/job-requisitions/bulk/status`, {
        ids,
        status,
        updatedBy: getCurrentUserId()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getNextCode: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-requisitions/next-code`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// CANDIDATES API
// ========================================
export const candidateApi = {
  getAll: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/candidates`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getByRequisition: async (requisitionId, params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/requisition/${requisitionId}`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  create: async (data) => {
    try {
      const payload = {
        ...data,
        createdBy: getCurrentUserId()
      };
      const response = await axiosInstance.post(`${API_BASE}/api/candidates`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const payload = {
        ...data,
        updatedBy: getCurrentUserId()
      };
      const response = await axiosInstance.put(`${API_BASE}/api/candidates/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/candidates/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateStage: async (id, stage, remarks = '') => {
    try {
      const response = await axiosInstance.patch(`${API_BASE}/api/candidates/${id}/stage`, {
        stage,
        updatedBy: getCurrentUserId(),
        remarks
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  markAsJoined: async (id, dateOfJoining) => {
    try {
      const response = await axiosInstance.patch(`${API_BASE}/api/candidates/${id}/joined`, {
        dateOfJoining,
        updatedBy: getCurrentUserId()
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getPipelineSummary: async (requisitionId = null) => {
    try {
      const params = requisitionId ? { requisitionId } : {};
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/pipeline/summary`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStageHistory: async (id) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/${id}/history`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSourceMix: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/dashboard/source-mix`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getTATAnalytics: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/dashboard/tat`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getRecruiterPerformance: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/dashboard/recruiter-performance`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getJoinersSummary: async (startDate, endDate, verticalId = null) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (verticalId) params.verticalId = verticalId;
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/dashboard/joiners-summary`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreate: async (candidates) => {
    try {
      const payload = candidates.map(c => ({
        ...c,
        createdBy: getCurrentUserId()
      }));
      const response = await axiosInstance.post(`${API_BASE}/api/candidates/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkUpdateStage: async (ids, stage, remarks = '') => {
    try {
      const response = await axiosInstance.patch(`${API_BASE}/api/candidates/bulk/stage`, {
        ids,
        stage,
        updatedBy: getCurrentUserId(),
        remarks
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getStages: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/candidates/stages`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// USERS API (for recruiter/HR user assignment - uses existing user management)
// Recruiters are managed through Role Management (RECRUITER, SR_RECRUITER, HR_HEAD, HIRING_MANAGER roles)
// ========================================
export const usersApi = {
  // Get all users for dropdown - uses existing /admin/users endpoint
  getAll: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/admin/users`, {
        params: {
          company: 'Corrohealth',
          page: params.page || 0,
          size: params.size || 100,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get dropdown list of users for recruiter/HR selection
  // Returns simplified list with id and name for dropdown
  getDropdown: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/admin/users`, {
        params: {
          company: 'Corrohealth',
          page: 0,
          size: 500, // Get all users for dropdown
          sortBy: 'firstName',
          sortDirection: 'asc'
        }
      });
      // Transform response to dropdown format: { id, name }
      const data = response.data?.data;
      if (data?.content) {
        return {
          ...response.data,
          data: data.content.map(user => ({
            id: user.id,
            name: `${user.firstName} ${user.secondName}`.trim(),
            email: user.email,
            empId: user.empId,
            department: user.department,
            role: user.role
          }))
        };
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get users with recruiter/HR roles for assignment dropdowns
  getRecruiters: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/admin/users`, {
        params: {
          company: 'Corrohealth',
          page: 0,
          size: 500,
          sortBy: 'firstName',
          sortDirection: 'asc'
        }
      });
      const data = response.data?.data;
      const hrRoles = ['RECRUITER', 'SR_RECRUITER', 'HR_HEAD', 'HIRING_MANAGER', 'ADMIN', 'SUPER_ADMIN'];
      if (data?.content) {
        // Filter to only show users with HR/Admin roles
        const recruiters = data.content.filter(user => hrRoles.includes(user.role));
        return {
          ...response.data,
          data: recruiters.map(user => ({
            id: user.id,
            name: `${user.firstName} ${user.secondName}`.trim(),
            email: user.email,
            empId: user.empId,
            department: user.department,
            role: user.role
          }))
        };
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get user by ID
  getById: async (id) => {
    try {
      // Use the users list and filter - no direct getById endpoint in AdminController
      const response = await axiosInstance.get(`${API_BASE}/admin/users`, {
        params: {
          company: 'Corrohealth',
          page: 0,
          size: 500
        }
      });
      const data = response.data?.data;
      if (data?.content) {
        const user = data.content.find(u => u.id === id);
        if (user) {
          return { ...response.data, data: user };
        }
      }
      throw new Error('User not found');
    } catch (error) {
      throw error;
    }
  },

  // Get all roles - uses Role enum values
  getRoles: async () => {
    try {
      // Return hardcoded roles since they come from the Role enum
      return {
        status: 200,
        message: 'Roles fetched successfully',
        data: [
          'SUPER_ADMIN', 'ADMIN', 'USER', 'GATEMEN', 'REGISTERED_USER',
          'RECRUITER', 'SR_RECRUITER', 'HR_HEAD', 'HIRING_MANAGER'
        ]
      };
    } catch (error) {
      throw error;
    }
  },

  // Get HR/Recruitment specific roles
  getHRRoles: async () => {
    try {
      return {
        status: 200,
        message: 'HR Roles fetched successfully',
        data: ['RECRUITER', 'SR_RECRUITER', 'HR_HEAD', 'HIRING_MANAGER']
      };
    } catch (error) {
      throw error;
    }
  }
};

// Backward compatibility alias
export const recruiterApi = usersApi;

// ========================================
// JOB MASTER DATA API
// ========================================
export const jobMasterApi = {
  // Verticals
  getVerticals: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/verticals`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getVerticalsDropdown: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/verticals/dropdown`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getVerticalById: async (id) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/verticals/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createVertical: async (data) => {
    try {
      const payload = { ...data, createdBy: getCurrentUserId() };
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/verticals`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateVertical: async (id, data) => {
    try {
      const payload = { ...data, updatedBy: getCurrentUserId() };
      const response = await axiosInstance.put(`${API_BASE}/api/job-master/verticals/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteVertical: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/job-master/verticals/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreateVerticals: async (verticals) => {
    try {
      const payload = verticals.map(v => ({ ...v, createdBy: getCurrentUserId() }));
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/verticals/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Specialties
  getSpecialties: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/specialties`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getSpecialtiesDropdown: async (verticalId = null) => {
    try {
      const params = verticalId ? { verticalId } : {};
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/specialties/dropdown`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createSpecialty: async (data) => {
    try {
      const payload = { ...data, createdBy: getCurrentUserId() };
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/specialties`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSpecialty: async (id, data) => {
    try {
      const payload = { ...data, updatedBy: getCurrentUserId() };
      const response = await axiosInstance.put(`${API_BASE}/api/job-master/specialties/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteSpecialty: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/job-master/specialties/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreateSpecialties: async (specialties) => {
    try {
      const payload = specialties.map(s => ({ ...s, createdBy: getCurrentUserId() }));
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/specialties/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Designations
  getDesignations: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/designations`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getDesignationsDropdown: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/designations/dropdown`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createDesignation: async (data) => {
    try {
      const payload = { ...data, createdBy: getCurrentUserId() };
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/designations`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateDesignation: async (id, data) => {
    try {
      const payload = { ...data, updatedBy: getCurrentUserId() };
      const response = await axiosInstance.put(`${API_BASE}/api/job-master/designations/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteDesignation: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/job-master/designations/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreateDesignations: async (designations) => {
    try {
      const payload = designations.map(d => ({ ...d, createdBy: getCurrentUserId() }));
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/designations/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Projects
  getProjects: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/projects`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getProjectsDropdown: async (verticalId = null) => {
    try {
      const params = verticalId ? { verticalId } : {};
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/projects/dropdown`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createProject: async (data) => {
    try {
      const payload = { ...data, createdBy: getCurrentUserId() };
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/projects`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateProject: async (id, data) => {
    try {
      const payload = { ...data, updatedBy: getCurrentUserId() };
      const response = await axiosInstance.put(`${API_BASE}/api/job-master/projects/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteProject: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/job-master/projects/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreateProjects: async (projects) => {
    try {
      const payload = projects.map(p => ({ ...p, createdBy: getCurrentUserId() }));
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/projects/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Cost Centers
  getCostCenters: async (params = {}) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/cost-centers`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getCostCentersDropdown: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/job-master/cost-centers/dropdown`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createCostCenter: async (data) => {
    try {
      const payload = { ...data, createdBy: getCurrentUserId() };
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/cost-centers`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateCostCenter: async (id, data) => {
    try {
      const payload = { ...data, updatedBy: getCurrentUserId() };
      const response = await axiosInstance.put(`${API_BASE}/api/job-master/cost-centers/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteCostCenter: async (id) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/job-master/cost-centers/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  bulkCreateCostCenters: async (costCenters) => {
    try {
      const payload = costCenters.map(c => ({ ...c, createdBy: getCurrentUserId() }));
      const response = await axiosInstance.post(`${API_BASE}/api/job-master/cost-centers/bulk`, payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// JOB DASHBOARD API
// ========================================
export const jobDashboardApi = {
  // Get overview stats
  getOverview: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/overview`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get segment-wise data
  getSegments: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/segments`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get project-wise data
  getProjects: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/projects`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get recruiter performance
  getRecruiters: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/recruiters`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get TAT analysis
  getTAT: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/tat`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get source mix
  getSourceMix: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/source-mix`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get full dashboard data
  getFullDashboard: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/full`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Download endpoints - returns blob
  downloadOverview: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/download/overview`, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  downloadSegments: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/download/segments`, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  downloadProjects: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/download/projects`, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  downloadRecruiters: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/download/recruiters`, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  downloadTAT: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/download/tat`, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  },

  downloadSourceMix: async (startDate, endDate) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await axiosInstance.get(`${API_BASE}/api/job-dashboard/download/source-mix`, {
        params,
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// JOB BULK UPLOAD API (Excel)
// ========================================
export const jobBulkUploadApi = {
  // Upload Excel file with JR Sample and Pipeline Sample sheets
  uploadExcel: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(`${API_BASE}/api/jobs/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Validate Excel file without processing
  validateExcel: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosInstance.post(`${API_BASE}/api/jobs/bulk-upload/validate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get template info (expected columns)
  getTemplateInfo: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/jobs/bulk-upload/template-info`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// ========================================
// RECRUITER ASSIGNMENT CONFIG API
// ========================================
export const recruiterAssignmentApi = {
  // Get global assignment settings
  getSettings: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/settings`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update global assignment settings
  updateSettings: async (settings) => {
    try {
      const response = await axiosInstance.put(`${API_BASE}/api/assignment-config/settings`, settings);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all recruiter configurations
  getRecruiterConfigs: async (query = null) => {
    try {
      const params = query ? { query } : {};
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/recruiters`, { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get recruiter config by ID
  getRecruiterConfig: async (recruiterId) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/recruiters/${recruiterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create recruiter configuration
  createRecruiterConfig: async (data) => {
    try {
      const response = await axiosInstance.post(`${API_BASE}/api/assignment-config/recruiters`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update recruiter configuration
  updateRecruiterConfig: async (recruiterId, data) => {
    try {
      const response = await axiosInstance.put(`${API_BASE}/api/assignment-config/recruiters/${recruiterId}`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete (deactivate) recruiter configuration
  deleteRecruiterConfig: async (recruiterId) => {
    try {
      const response = await axiosInstance.delete(`${API_BASE}/api/assignment-config/recruiters/${recruiterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get available recruiters (users with HR roles who can be configured)
  getAvailableRecruiters: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/available-recruiters`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get assignment logs with pagination
  getAssignmentLogs: async (page = 0, size = 50) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/logs`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get assignment logs for a specific recruiter
  getRecruiterLogs: async (recruiterId) => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/logs/recruiter/${recruiterId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get assignment statistics
  getStats: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/stats`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get capacity alerts (recruiters approaching/at capacity)
  getCapacityAlerts: async () => {
    try {
      const response = await axiosInstance.get(`${API_BASE}/api/assignment-config/capacity-alerts`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Export all Job Management APIs
export default {
  jobRequisitionApi,
  candidateApi,
  usersApi,
  recruiterApi,  // Backward compatibility alias
  jobMasterApi,
  jobDashboardApi,
  jobBulkUploadApi,
  recruiterAssignmentApi
};
